type VideoWithFrameCallback = HTMLVideoElement & {
  requestVideoFrameCallback?: (
    callback: (
      now: DOMHighResTimeStamp,
      metadata: { mediaTime: number },
    ) => void,
  ) => number
  cancelVideoFrameCallback?: (handle: number) => void
}

export type CameraFrameReadinessResult = {
  ready: boolean
  checkedFrames: number
  consecutiveReadyFrames: number
  elapsedMs: number
  reason: 'visible-frame' | 'timeout' | 'track-ended' | 'play-failed'
}

const SAMPLE_SIZE = 16
const DEFAULT_CONSECUTIVE_FRAMES = 2
const DEFAULT_TIMEOUT_MS = 1000
const MIN_AVERAGE_LUMINANCE = 5
const MIN_LUMINANCE_DEVIATION = 3
const VISIBLE_PIXEL_LUMINANCE = 12
const MIN_VISIBLE_PIXEL_RATIO = 0.02

/** Distinguish a real (possibly dark) camera image from a uniform startup black frame. */
export function isCameraFrameVisuallyReady(pixels: Uint8ClampedArray): boolean {
  const pixelCount = Math.floor(pixels.length / 4)
  if (pixelCount === 0) {
    return false
  }

  let luminanceSum = 0
  let luminanceSquareSum = 0
  let visiblePixels = 0

  for (let offset = 0; offset < pixelCount * 4; offset += 4) {
    const luminance =
      pixels[offset] * 0.2126
      + pixels[offset + 1] * 0.7152
      + pixels[offset + 2] * 0.0722

    luminanceSum += luminance
    luminanceSquareSum += luminance * luminance
    if (luminance > VISIBLE_PIXEL_LUMINANCE) {
      visiblePixels += 1
    }
  }

  const averageLuminance = luminanceSum / pixelCount
  const variance = Math.max(
    0,
    luminanceSquareSum / pixelCount - averageLuminance * averageLuminance,
  )
  const luminanceDeviation = Math.sqrt(variance)
  const visiblePixelRatio = visiblePixels / pixelCount

  return averageLuminance > MIN_AVERAGE_LUMINANCE
    || luminanceDeviation > MIN_LUMINANCE_DEVIATION
    || visiblePixelRatio > MIN_VISIBLE_PIXEL_RATIO
}

/**
 * Wait until the local camera produces consecutive non-black frames before publishing it.
 * Timeout is deliberately bounded so a covered camera or genuinely dark room cannot block startup.
 */
export async function waitForCameraTrackReady(
  track: MediaStreamTrack,
  options: { consecutiveFrames?: number; timeoutMs?: number } = {},
): Promise<CameraFrameReadinessResult> {
  const requiredFrames = Math.max(
    1,
    Math.floor(options.consecutiveFrames ?? DEFAULT_CONSECUTIVE_FRAMES),
  )
  const timeoutMs = Math.max(0, options.timeoutMs ?? DEFAULT_TIMEOUT_MS)
  const startedAt = performance.now()

  if (track.readyState === 'ended') {
    return {
      ready: false,
      checkedFrames: 0,
      consecutiveReadyFrames: 0,
      elapsedMs: 0,
      reason: 'track-ended',
    }
  }

  return new Promise((resolve) => {
    const video = document.createElement('video') as VideoWithFrameCallback
    const canvas = document.createElement('canvas')
    const context = canvas.getContext('2d', { willReadFrequently: true })
    let checkedFrames = 0
    let consecutiveReadyFrames = 0
    let settled = false
    let frameCallbackHandle: number | null = null
    let animationFrameHandle: number | null = null
    let lastFallbackMediaTime = -1

    canvas.width = SAMPLE_SIZE
    canvas.height = SAMPLE_SIZE
    video.muted = true
    video.playsInline = true
    video.autoplay = true
    video.srcObject = new MediaStream([track])
    video.style.cssText =
      'position:fixed;left:-9999px;top:-9999px;width:1px;height:1px;opacity:0;pointer-events:none;'
    document.body.appendChild(video)

    const finish = (
      ready: boolean,
      reason: CameraFrameReadinessResult['reason'],
    ) => {
      if (settled) {
        return
      }
      settled = true
      clearTimeout(timeoutHandle)
      if (frameCallbackHandle !== null) {
        video.cancelVideoFrameCallback?.(frameCallbackHandle)
      }
      if (animationFrameHandle !== null) {
        window.cancelAnimationFrame(animationFrameHandle)
      }
      video.pause()
      video.srcObject = null
      video.remove()
      resolve({
        ready,
        checkedFrames,
        consecutiveReadyFrames,
        elapsedMs: Number((performance.now() - startedAt).toFixed(1)),
        reason,
      })
    }

    const inspectFrame = () => {
      if (!context || video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) {
        return
      }

      try {
        context.drawImage(video, 0, 0, SAMPLE_SIZE, SAMPLE_SIZE)
        const pixels = context.getImageData(0, 0, SAMPLE_SIZE, SAMPLE_SIZE).data
        checkedFrames += 1
        consecutiveReadyFrames = isCameraFrameVisuallyReady(pixels)
          ? consecutiveReadyFrames + 1
          : 0
        if (consecutiveReadyFrames >= requiredFrames) {
          finish(true, 'visible-frame')
        }
      } catch {
        // The timeout remains the safe fallback if this browser cannot sample the frame.
      }
    }

    const scheduleFrameCheck = () => {
      if (settled) {
        return
      }

      if (video.requestVideoFrameCallback) {
        frameCallbackHandle = video.requestVideoFrameCallback((_now, metadata) => {
          lastFallbackMediaTime = metadata.mediaTime
          inspectFrame()
          scheduleFrameCheck()
        })
        return
      }

      animationFrameHandle = window.requestAnimationFrame(() => {
        if (video.currentTime !== lastFallbackMediaTime) {
          lastFallbackMediaTime = video.currentTime
          inspectFrame()
        }
        scheduleFrameCheck()
      })
    }

    const timeoutHandle = window.setTimeout(
      () => finish(false, 'timeout'),
      timeoutMs,
    )

    void video.play()
      .then(scheduleFrameCheck)
      .catch(() => finish(false, 'play-failed'))
  })
}
